import { VehicleModelWithInsurancesForD2CDto } from '@orcar/common';
import {
  CAR_FUEL,
  CAR_OPTION,
  CheckboxFilter,
  cn,
  ColoredSurfaceWithIconButton,
  filterInsuranceAtom,
  filterOptionAtom,
  FilterOptionsType,
  IconV2,
  INSURANCE_TYPE,
  SliderFilter,
  useRecoil,
} from '@orcar/common-d2c';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { INSURANCE_BUTTONS } from '@/constants/insurance';
import { CAR_TYPE } from '@/constants/vehicle-model';
import { getDefaultFilterOptions } from '@/utils/filterOptions';
import {
  filterVehiclesByName,
  getFilteredSearchResult,
} from '@/utils/vehicle-model';
import InsuranceInfoModal from './InsuranceInfoModal';

type SearchFiltersProps = {
  vehicleModelList?: VehicleModelWithInsurancesForD2CDto[];
  /** 차량명(모델명) 검색어 — 입력 즉시 결과에 반영. */
  nameQuery?: string;
  onNameQueryChange?: (value: string) => void;
};

const SearchFilters = ({
  vehicleModelList,
  nameQuery,
  onNameQueryChange,
}: SearchFiltersProps) => {
  const {
    recoilState: [recoilFilterOption, setRecoilFilterOption],
  } = useRecoil(filterOptionAtom);
  const {
    recoilState: [recoilInsurance, setRecoilInsurance],
  } = useRecoil(filterInsuranceAtom);

  const [insurance, setInsurance] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(0);
  const [minYear, setMinYear] = useState<number>(0);
  const [maxYear, setMaxYear] = useState<number>(0);
  const [carTypeList, setCarTypeList] = useState<string[]>([]);
  const [carFuelList, setCarFuelList] = useState<string[]>([]);
  const [optionList, setOptionList] = useState<string[]>([]);
  const [openInsuranceModal, setOpenInsuranceModal] = useState(false);

  // "펫과 함께"(반려동물 동반) 토글 — 차종이 아니라 isWithPet(URL) 모드이므로
  // 검색필터 차종 섹션에 별도 토글로 노출한다. (펫 탭과 동일한 URL 파라미터를 공유)
  const [searchParams, setSearchParams] = useSearchParams();
  const isWithPet = searchParams.get('isWithPet') === 'true';
  // 펫 모드에서는 일반면책를 제외하고 고급면책/고급면책+무특만 노출한다.
  const insuranceButtons = isWithPet
    ? INSURANCE_BUTTONS.filter((button) => button.id !== INSURANCE_TYPE.NORMAL)
    : INSURANCE_BUTTONS;
  const handleTogglePet = () => {
    const next = new URLSearchParams(searchParams);
    if (isWithPet) next.delete('isWithPet');
    else next.set('isWithPet', 'true');
    // 펫 토글 시 차종/연료/옵션/보험 필터를 초기화한다.
    // (초기화하지 않으면 직전 차종 필터가 남아 펫 결과가 그 차종으로 제한됨 — 펫 탭과 동일 동작)
    setRecoilFilterOption({
      minPrice: null,
      maxPrice: null,
      minYear: null,
      maxYear: null,
      carFuelList: [],
      carTypeList: [],
      optionList: [],
    });
    setRecoilInsurance([]);
    window.scrollTo({ top: 0 });
    setSearchParams(next);
  };

  const localFilterResult = filterVehiclesByName(
    getFilteredSearchResult(
      vehicleModelList,
      {
        minPrice,
        maxPrice,
        minYear,
        maxYear,
        carTypeList,
        carFuelList,
        optionList,
      },
      insurance,
    ),
    nameQuery ?? '',
  );

  const defaultFilterOptions = useMemo(
    () => getDefaultFilterOptions(vehicleModelList),
    [vehicleModelList],
  );

  useEffect(() => {
    const {
      minPrice,
      maxPrice,
      minYear,
      maxYear,
      carTypeList,
      carFuelList,
      optionList,
    } = recoilFilterOption;

    setMinPrice(minPrice || defaultFilterOptions.minPrice);
    setMaxPrice(maxPrice || defaultFilterOptions.maxPrice);
    setMinYear(minYear || defaultFilterOptions.minYear);
    setMaxYear(maxYear || defaultFilterOptions.maxYear);
    setCarTypeList(carTypeList);
    setOptionList(optionList);
    setCarFuelList(carFuelList);
  }, [recoilFilterOption, defaultFilterOptions]);

  useEffect(() => {
    setInsurance(recoilInsurance);
  }, [recoilInsurance]);

  const handleReset = () => {
    setMinPrice(defaultFilterOptions.minPrice);
    setMaxPrice(defaultFilterOptions.maxPrice);
    setMinYear(defaultFilterOptions.minYear);
    setMaxYear(defaultFilterOptions.maxYear);
    setCarTypeList([]);
    setCarFuelList([]);
    setOptionList([]);
    setInsurance([]);
    onNameQueryChange?.('');
  };

  const handleApplyFilter = (
    {
      minPrice,
      maxPrice,
      minYear,
      maxYear,
      carTypeList,
      carFuelList,
      optionList,
    }: FilterOptionsType,
    insurance: string[],
  ) => {
    window.scrollTo({ top: 0 });

    // "펫과 함께"를 하나의 차종처럼 취급한다. (상호배타)
    // 차종/연료/옵션/보험 필터를 적용하면 펫 모드를 해제해 쿼리에 isWithPet이 남지 않게 한다.
    if (
      isWithPet &&
      (carTypeList.length > 0 ||
        carFuelList.length > 0 ||
        optionList.length > 0 ||
        insurance.length > 0)
    ) {
      const next = new URLSearchParams(searchParams);
      next.delete('isWithPet');
      setSearchParams(next);
    }

    setRecoilFilterOption({
      minPrice: minPrice === defaultFilterOptions.minPrice ? null : minPrice,
      maxPrice: maxPrice === defaultFilterOptions.maxPrice ? null : maxPrice,
      minYear: minYear === defaultFilterOptions.minYear ? null : minYear,
      maxYear: maxYear === defaultFilterOptions.maxYear ? null : maxYear,
      carTypeList,
      carFuelList,
      optionList,
    });
    setRecoilInsurance(insurance);
  };

  return (
    <>
      <div className='scrollbar-thin relative flex h-[calc(100vh-349px)] w-[292px] flex-col gap-6 overflow-hidden overflow-y-scroll rounded-m border-x border-t border-grey-010  bg-grey-000'>
        <div className='sticky  bg-grey-000 px-5 pt-8'>
          <p className='sub1 text-grey-090'>검색필터</p>
        </div>
        <div className='flex flex-col gap-9 px-5'>
          <div className='flex flex-col gap-4'>
            <p className='sub3 text-grey-070'>차량 검색</p>
            <div className='relative'>
              <input
                type='text'
                value={nameQuery ?? ''}
                onChange={(e) => onNameQueryChange?.(e.target.value)}
                placeholder='모델명 검색 (예: K5)'
                className='body2 w-full rounded-s border border-grey-020 bg-grey-000 py-2.5 pl-4 pr-9 text-grey-090 placeholder:text-grey-030 focus:border-primary-050 focus:outline-none'
              />
              {!!nameQuery && (
                <button
                  type='button'
                  aria-label='검색어 지우기'
                  onClick={() => onNameQueryChange?.('')}
                  className='absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-grey-040 hover:bg-grey-010'
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex flex-col gap-4'>
            <div className='flex '>
              <p className='sub3 text-grey-070'>보험</p>
              <div
                className='size-6 cursor-pointer'
                onClick={() => setOpenInsuranceModal(true)}
              >
                <IconV2 icon='info' />
              </div>
            </div>
            <div className='flex flex-col gap-5'>
              <CheckboxFilter
                checkboxItemList={insuranceButtons}
                checkedList={insurance}
                setCheckedList={(value) => setInsurance(value)}
                itemCountPerRow={1}
              />
            </div>
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex flex-col gap-4'>
            <p className='sub3 text-grey-070'>차량요금 (보험료 별도)</p>
            <div className='flex flex-col gap-5'>
              <SliderFilter
                value={[minPrice, maxPrice]}
                min={defaultFilterOptions.minPrice}
                max={defaultFilterOptions.maxPrice}
                rangeText={`${minPrice.toLocaleString()}원 ~ ${maxPrice.toLocaleString()}원`}
                step={1000}
                setValue={(value) => {
                  const [min, max] = value;
                  setMinPrice(min);
                  setMaxPrice(max);
                }}
              />
            </div>
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex flex-col gap-4'>
            <p className='sub3 text-grey-070'>차종</p>
            <CheckboxFilter
              checkboxItemList={CAR_TYPE}
              checkedList={carTypeList}
              setCheckedList={(value) => setCarTypeList(value)}
            />
            {/* 펫과 함께 토글 — 차종 섹션 내 별도 노출 (isWithPet 모드) */}
            <button
              type='button'
              onClick={handleTogglePet}
              className='flex items-center gap-2 text-left'
            >
              <span
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-[4px] border',
                  isWithPet
                    ? 'border-primary-050 bg-primary-050'
                    : 'border-grey-030 bg-grey-000',
                )}
              >
                {isWithPet && (
                  <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
                    <path
                      d='M2.5 6.2L4.8 8.5L9.5 3.5'
                      stroke='white'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
              </span>
              <span
                className={cn(
                  'body2',
                  isWithPet ? 'font-bold text-primary-050' : 'text-grey-090',
                )}
              >
                🐾 펫과 함께
              </span>
            </button>
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex flex-col gap-4'>
            <p className='sub3 text-grey-070'>연료</p>
            <CheckboxFilter
              checkboxItemList={CAR_FUEL}
              checkedList={carFuelList}
              setCheckedList={(value) => setCarFuelList(value)}
            />
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex flex-col gap-4'>
            <p className='sub3 text-grey-070'>연식</p>
            <div className='flex flex-col gap-5'>
              <SliderFilter
                value={[minYear, maxYear]}
                min={defaultFilterOptions.minYear}
                max={defaultFilterOptions.maxYear}
                step={1}
                rangeText={`${minYear}년 ~ ${maxYear}년`}
                setValue={(value) => {
                  const [min, max] = value;
                  setMinYear(min);
                  setMaxYear(max);
                }}
              />
            </div>
          </div>

          <div className='h-px bg-grey-010 ' />

          <div className='flex  flex-col gap-4'>
            <p className='sub3 text-grey-070'>추가옵션</p>
            <CheckboxFilter
              checkboxItemList={CAR_OPTION}
              checkedList={optionList}
              setCheckedList={(value) => setOptionList(value)}
              itemCountPerRow={2}
            />
          </div>
        </div>
      </div>
      <div className='bottom-0 flex justify-between gap-2 rounded-b-m border-x border-b border-grey-010 bg-grey-000 px-5 pb-8 pt-6'>
        <button
          className='flex w-[61px] items-center justify-center text-nowrap rounded-s border border-solid border-primary-050 px-8 py-2 text-primary-050'
          onClick={handleReset}
        >
          초기화
        </button>
        <ColoredSurfaceWithIconButton
          type='default'
          title={`${localFilterResult?.length || 0}개의 차량 보기`}
          onClick={() =>
            handleApplyFilter(
              {
                minPrice,
                maxPrice,
                minYear,
                maxYear,
                carTypeList,
                carFuelList,
                optionList,
              },
              insurance,
            )
          }
        />
      </div>
      {openInsuranceModal && (
        <InsuranceInfoModal onClose={() => setOpenInsuranceModal(false)} />
      )}
    </>
  );
};

export default SearchFilters;
